import { useState, useEffect } from 'react';
import { Navbar } from '@/components/layout/Navbar';
import { InstanceFooter } from '@/components/layout/InstanceFooter';
import { statsApi } from '@/services/api';
import {
  PlusCircleIcon,
  PencilSquareIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';
import { useAppStore } from '@/stores/appStore';
import {
  productApi,
  subProductApi,
  initiativeApi,
  statusApi,
  fiscalYearApi,
  labsApi,
} from '@/services/api';

interface AuditLogEntry {
  id: string;
  action: 'created' | 'updated';
  fieldName: string | null;
  oldValue: string | null;
  newValue: string | null;
  changedAt: string;
  initiative: {
    id: string;
    title: string;
    subProduct: {
      id: string;
      name: string;
      product: {
        id: string;
        name: string;
      };
    };
  };
}

function formatDateTime(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function TruncatedValue({
  value,
  className
}: {
  value: string;
  className?: string;
}) {
  const needsTruncation = value.length > 50;

  return (
    <span
      className={`${className} ${needsTruncation ? 'truncate max-w-[200px] inline-block align-bottom cursor-help' : ''}`}
      title={needsTruncation ? value : undefined}
    >
      {value}
    </span>
  );
}

function ChangeRow({
  fieldName,
  oldValue,
  newValue,
}: {
  fieldName: string;
  oldValue: string | null;
  newValue: string | null;
}) {
  return (
    <div className="flex items-center gap-2 text-sm min-w-0">
      <span className="text-gray-500 font-medium flex-shrink-0">{fieldName}:</span>
      {oldValue && (
        <TruncatedValue value={oldValue} className="text-gray-400 line-through" />
      )}
      {oldValue && newValue && (
        <ArrowRightIcon className="w-3 h-3 text-gray-400 flex-shrink-0" />
      )}
      {newValue && (
        <TruncatedValue value={newValue} className="text-gray-700" />
      )}
      {!oldValue && newValue && (
        <span className="text-green-600 text-xs flex-shrink-0">(added)</span>
      )}
      {oldValue && !newValue && (
        <span className="text-red-600 text-xs flex-shrink-0">(removed)</span>
      )}
    </div>
  );
}

const PAGE_SIZE = 50;

export function AuditLogPage() {
  const [entries, setEntries] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const {
    initiatives,
    setProducts,
    setInitiatives,
    setStatuses,
    setQuarters,
    setLabsFeatures,
    openViewInitiativeModal,
  } = useAppStore();

  // Load reference data
  useEffect(() => {
    const loadData = async () => {
      try {
        const [
          productsData,
          subProductsData,
          initiativesData,
          statusesData,
          fiscalYearsData,
          labsData,
        ] = await Promise.all([
          productApi.getAll(),
          subProductApi.getAll(),
          initiativeApi.getAll(),
          statusApi.getAll(),
          fiscalYearApi.getAll(),
          labsApi.getAll().catch(() => []),
        ]);

        const productsWithSubProducts = productsData.map((product: any) => ({
          ...product,
          subProducts: subProductsData.filter(
            (sp: any) => sp.productId === product.id
          ),
        }));

        setProducts(productsWithSubProducts);
        setInitiatives(initiativesData);
        setStatuses(statusesData);

        const allQuarters = fiscalYearsData
          .flatMap((fy: any) => fy.quarters || [])
          .sort((a: any, b: any) => a.displayOrder - b.displayOrder);
        setQuarters(allQuarters);
        if (labsData.length > 0) setLabsFeatures(labsData);
      } catch (error) {
        console.error('Failed to load reference data:', error);
      }
    };

    loadData();
  }, [setProducts, setInitiatives, setStatuses, setQuarters, setLabsFeatures]);

  // Fetch initial audit log entries
  useEffect(() => {
    const fetchAuditLog = async () => {
      setLoading(true);
      try {
        const response = await statsApi.getRecentActivity(PAGE_SIZE, 0);
        setEntries(response.data);
        setHasMore(response.hasMore);
        setTotal(response.total);
      } catch (error) {
        console.error('Failed to fetch audit log:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAuditLog();
  }, []);

  // Load more entries
  const handleLoadMore = async () => {
    setLoadingMore(true);
    try {
      const response = await statsApi.getRecentActivity(PAGE_SIZE, entries.length);
      setEntries((prev) => [...prev, ...response.data]);
      setHasMore(response.hasMore);
    } catch (error) {
      console.error('Failed to load more entries:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleInitiativeClick = (initiativeId: string) => {
    const initiative = initiatives.find((i) => i.id === initiativeId);
    if (initiative) {
      openViewInitiativeModal(initiative);
    }
  };

  // Group entries by date
  const groupedByDate = entries.reduce((acc, entry) => {
    const date = new Date(entry.changedAt).toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(entry);
    return acc;
  }, {} as Record<string, AuditLogEntry[]>);

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      <Navbar />

      <main className="flex-1 overflow-auto">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-6">Audit Log</h1>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : entries.length === 0 ? (
            <div className="text-center py-12">
              <PencilSquareIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No activity recorded yet</p>
            </div>
          ) : (
            <>
              {/* Entry count */}
              <div className="text-sm text-gray-500 mb-4">
                Showing {entries.length} of {total} entries
              </div>

              <div className="space-y-8">
                {Object.entries(groupedByDate).map(([date, dateEntries]) => (
                  <div key={date}>
                    <h2 className="text-sm font-medium text-gray-500 mb-3">{date}</h2>
                    <div className="space-y-3">
                      {dateEntries.map((entry) => (
                        <button
                          key={entry.id}
                          onClick={() => handleInitiativeClick(entry.initiative.id)}
                          className="w-full text-left bg-white rounded-lg shadow-sm p-4 hover:shadow-md transition-all"
                        >
                          <div className="flex items-start gap-3">
                            {entry.action === 'created' ? (
                              <PlusCircleIcon className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                            ) : (
                              <PencilSquareIcon className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between gap-2 mb-1">
                                <span className="font-medium text-gray-900 truncate">
                                  {entry.initiative.title}
                                </span>
                                <span className="text-xs text-gray-400 flex-shrink-0">
                                  {formatDateTime(entry.changedAt)}
                                </span>
                              </div>
                              <div
                                className="text-sm text-gray-500 mb-2 truncate"
                                title={`${entry.initiative.subProduct.product.name} > ${entry.initiative.subProduct.name}`}
                              >
                                {entry.initiative.subProduct.product.name} &gt;{' '}
                                {entry.initiative.subProduct.name}
                              </div>
                              {entry.action === 'created' ? (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
                                  Created
                                </span>
                              ) : entry.fieldName ? (
                                <ChangeRow
                                  fieldName={entry.fieldName}
                                  oldValue={entry.oldValue}
                                  newValue={entry.newValue}
                                />
                              ) : null}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Load More button */}
              {hasMore && (
                <div className="mt-8 text-center">
                  <button
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    className="px-6 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {loadingMore ? (
                      <span className="flex items-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                        Loading...
                      </span>
                    ) : (
                      'Load More'
                    )}
                  </button>
                </div>
              )}

              {/* End of list message */}
              {!hasMore && entries.length > 0 && (
                <div className="mt-8 text-center text-sm text-gray-400">
                  No more entries
                </div>
              )}
            </>
          )}
        </div>
      </main>

      <InstanceFooter />
    </div>
  );
}
